<template>
    <AccessLevel
        v-slot="{ denied }"
        :disable-for="['company-readonly']"
    >
        <BaseForm
            v-if="data"
            data-cy="place-form"
            @submit="onSubmit"
        >
            <div class="form-group row align-items-center">
                <label
                    class="col-form-label col-md-5"
                    for="name"
                    >{{ $t('Food handling place name') }}</label
                >
                <div class="col-md-7">
                    <BaseInput
                        id="name"
                        v-model="data.name"
                        :is-editable="!denied && isEditMode"
                        data-cy="name-field"
                        :errors="v$.data.name?.$errors"
                    />
                </div>
            </div>

            <div class="form-group row align-items-center">
                <label
                    class="col-form-label col-md-5"
                    for="address"
                    >{{ $t('address') }}</label
                >
                <div class="col-md-7">
                    <BaseInput
                        id="address"
                        v-model="data.address"
                        :is-editable="!denied && isEditMode"
                        data-cy="address-field"
                        :errors="v$.data.address?.$errors"
                    />
                </div>
            </div>

            <div class="form-group row align-items-center">
                <label
                    class="col-form-label col-md-5"
                    for="country"
                    >{{ $t('Country') }}</label
                >
                <div class="col-md-7">
                    <BaseSelect
                        id="country"
                        v-model="data.country"
                        :is-editable="!denied && isEditMode"
                        data-cy="country-field"
                        :options="countries"
                        :custom-label="(item) => $t(item.name)"
                        :placeholder="$t('Choose country')"
                        searchable
                        :errors="v$.data.country.$errors"
                    />
                </div>
            </div>

            <div
                v-if="hasStates"
                class="form-group row align-items-center"
            >
                <label
                    class="col-form-label col-md-5"
                    for="state"
                    >{{ $t('State') }}</label
                >
                <div class="col-md-7">
                    <BaseSelect
                        id="state"
                        v-model="data.state"
                        :is-editable="!denied && isEditMode"
                        data-cy="state-field"
                        :options="countryStates"
                        :custom-label="(item) => $t(item.name)"
                        :placeholder="$t('Choose state')"
                        searchable
                        :errors="v$.data.state?.$errors"
                    />
                </div>
            </div>

            <div class="form-group row align-items-center">
                <label
                    class="col-form-label col-md-5"
                    for="phone"
                    >{{ $t('Phone') }}</label
                >
                <div class="col-md-7">
                    <BaseInput
                        id="phone"
                        v-model="data.phone"
                        :is-editable="!denied && isEditMode"
                        :errors="v$.data.phone?.$errors"
                        data-cy="phone-field"
                    />
                </div>
            </div>

            <div class="form-group row align-items-center">
                <label
                    class="col-form-label col-md-5"
                    for="email"
                    >{{ $t('email') }}</label
                >
                <div class="col-md-7">
                    <BaseInput
                        id="email"
                        v-model="data.email"
                        :is-editable="!denied && isEditMode"
                        data-cy="email-field"
                        :errors="v$.data.email?.$errors"
                    />
                </div>
            </div>

            <div class="form-group row align-items-center">
                <label
                    class="col-form-label col-md-5 d-flex align-items-center"
                    for="notification-email"
                >
                    {{ $t('notification_email') }}
                    <i
                        v-b-tooltip.hover
                        class="fd-info cursor-pointer ml-1"
                        :title="$t('Add Multiple emails by using comma(,) as separation character')"
                    ></i>
                </label>
                <div class="col-md-7">
                    <BaseInput
                        id="notification-email"
                        v-model="data.notificationEmail"
                        :is-editable="!denied && isEditMode"
                        data-cy="notification-email-field"
                        :errors="v$.data.notificationEmail?.$errors"
                    />
                </div>
            </div>

            <div class="form-group row align-items-center">
                <label
                    class="col-form-label col-md-5"
                    for="representative-person"
                    >{{ $t('representative') }}</label
                >
                <div class="col-md-7">
                    <BaseInput
                        id="representative-person"
                        v-model="data.representativePerson"
                        :is-editable="!denied && isEditMode"
                        :errors="v$.data.representativePerson?.$errors"
                        data-cy="representative-person-field"
                    />
                </div>
            </div>

            <div class="form-group row align-items-center">
                <label
                    class="col-form-label col-md-5"
                    for="main-activity-category"
                    >{{ $t('business_activity') }}</label
                >
                <div class="col-md-7">
                    <BaseSelect
                        id="main-activity-category"
                        v-model="businessActivity"
                        :is-editable="!denied && isEditMode"
                        :options="businessActivities"
                        :custom-label="(item) => $t(item.name)"
                        :errors="v$.data.businessActivity?.$errors"
                    />
                </div>
            </div>

            <div class="form-group row align-items-center">
                <label
                    class="col-form-label col-md-5"
                    for="food-handling-place"
                    >{{ $t('food_handling_place') }}</label
                >
                <div class="col-md-7">
                    <BaseSelect
                        id="food-handling-place"
                        v-model="data.locationType"
                        :is-editable="!denied && isEditMode"
                        data-cy="location-type-field"
                        :options="locationTypes"
                        :custom-label="(item) => $t(item.name)"
                        :errors="v$.data.locationType?.$errors"
                    />
                </div>
            </div>

            <div class="form-group row align-items-center">
                <label
                    class="col-form-label col-md-5"
                    for="number-of-employees"
                    >{{ $t('number_of_employees') }}</label
                >
                <div class="col-md-7">
                    <BaseSelect
                        id="number-of-employees"
                        v-model="data.numberOfEmployees"
                        :is-editable="!denied && isEditMode"
                        data-cy="number-of-employees-field"
                        :options="employeeNumbersOptions"
                        :errors="v$.data.numberOfEmployees?.$errors"
                    />
                </div>
            </div>

            <div class="form-group row align-items-center">
                <label
                    class="col-form-label col-md-5"
                    for="preferred-language"
                    >{{ $t('Preferred language') }}</label
                >
                <div class="col-md-7">
                    <BaseSelect
                        id="preferred-language"
                        v-model="data.preferredLanguage"
                        :is-editable="!denied && isEditMode"
                        :options="availableLanguages"
                        :custom-label="(item) => item.name"
                        :placeholder="$t('Choose preferred language')"
                        :errors="v$.data.preferredLanguage?.$errors"
                    />
                </div>
            </div>

            <div class="form-group row align-items-center">
                <label
                    class="col-form-label col-md-5"
                    for="preferred-language"
                    >{{ $t('Time zone') }}</label
                >
                <div class="col-md-7">
                    <TimezoneSelect
                        v-model="data.timezone"
                        :is-edit-mode="!denied && isEditMode"
                        :errors="v$.data.timezone?.$errors"
                    />
                </div>
            </div>

            <slot></slot>

            <div class="form-group row align-items-center">
                <div
                    v-if="!denied"
                    class="col-sm-7 offset-md-5 space-related-out-b"
                >
                    <span
                        v-if="isEditMode"
                        class="d-flex"
                    >
                        <button
                            v-if="!hideCancel"
                            class="btn btn-secondary w-50 space-related-out-r"
                            @click="stopEditMode"
                        >
                            {{ $t('Cancel') }}
                        </button>
                        <button
                            id="company-creation-save"
                            type="submit"
                            class="btn btn-primary"
                            :class="!hideCancel ? 'space-related-out-l w-50' : 'w-100'"
                        >
                            {{ $t('save') }}
                        </button>
                    </span>
                    <button
                        v-else-if="!isEditMode"
                        id="company-creation-edit"
                        type="button"
                        class="btn btn-primary btn-block"
                        @click="startEditMode"
                    >
                        {{ $t('edit') }}
                    </button>
                </div>
            </div>
        </BaseForm>
    </AccessLevel>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { email, helpers, required, requiredIf } from '@vuelidate/validators';
import { mapGetters } from 'vuex';

import AccessLevel from '@components/AccessLevel';
import BaseForm from '@components/BaseForm';

import TimezoneSelect from '@views/SetUp/components/TimezoneSelect';

export default {
    name: 'PlaceForm',

    compatConfig: {
        MODE: 3,
        INSTANCE_EVENT_EMITTER: true, // bv-tooltip
    },

    components: {
        AccessLevel,
        BaseForm,
        TimezoneSelect,
    },

    props: {
        modelValue: {
            type: Object,
            default: () => ({}),
        },
        startInEditMode: {
            type: Boolean,
            default: false,
        },
        hideCancel: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['submit'],

    setup() {
        return { v$: useVuelidate() };
    },

    data() {
        return {
            data: {
                name: null,
                address: null,
                country: null,
                state: null,
                phone: null,
                email: null,
                notificationEmail: null,
                representativePerson: null,
                businessActivity: null,
                locationType: null,
                numberOfEmployees: null,
                preferredLanguage: null,
                timezone: null,
            },
            isEditMode: false,
        };
    },

    validations() {
        return {
            data: {
                name: { required },
                address: { required },
                country: { required },
                state: {
                    required: requiredIf(function () {
                        return this.hasStates;
                    }),
                },
                phone: { required },
                email: { required, email },
                notificationEmail: {
                    email: helpers.regex(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
                    multipleEmails: helpers.regex(
                        /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(,\s*[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})*)?$/
                    ),
                },
                representativePerson: { required },
                businessActivity: { required },
                locationType: { required },
                numberOfEmployees: { required },
                preferredLanguage: { required },
                timezone: { required },
            },
        };
    },

    computed: {
        ...mapGetters([
            'countries',
            'businessActivities',
            'selectedCompany',
            'availableLanguages',
            'canWritePlace',
            'countryStates',
        ]),
        businessActivity: {
            get() {
                if (!this.data.businessActivity) {
                    return this.businessActivities.find(({ id }) => id === this.modelValue?.business_activity?.id);
                }

                return this.data.businessActivity;
            },
            set(value) {
                this.data.businessActivity = value;
                this.data.locationType = null;
            },
        },
        locationTypes() {
            return this.businessActivity?.location_types || [];
        },
        employeeNumbersOptions() {
            return ['1-3', '4-14', '15+'];
        },
        country() {
            return this.data.country;
        },
        hasStates() {
            return !!this.countryStates?.length;
        },
    },

    watch: {
        country: async function (val) {
            if (val !== null) {
                await this.$store.dispatch('setCountryStates', val.id);
            }
        },
        modelValue: {
            immediate: true,
            async handler(value) {
                if (value) {
                    await this.$store.dispatch('setCountryStates', value?.country_id);
                    this.data.name = value.name;
                    this.data.address = value.address;
                    this.data.phone = value.person_phone;
                    this.data.email = value.person_email;
                    this.data.notificationEmail = value.notification_email;
                    this.data.representativePerson = value.contact_person;
                    this.data.businessActivity = value.business_activity;
                    this.data.locationType = value.location_type;
                    this.data.numberOfEmployees = value.number_of_employees;
                    this.data.timezone = value.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

                    if (value.preferred_language) {
                        this.data.preferredLanguage = this.availableLanguages.find(
                            (item) => item.key === value.preferred_language
                        );
                    } else {
                        this.data.preferredLanguage = this.availableLanguages.find(
                            (item) => item.key === this.$i18n.locale
                        );
                    }
                    if (value?.state_id) {
                        this.data.state = this.countryStates.find((item) => item.id === value.state_id);
                    }
                    if (value?.country_id) {
                        this.data.country = this.countries.find((item) => item.id === value.country_id);
                    } else {
                        this.data.country = this.countries.find(
                            (item) => item.id === this.selectedCompany?.company?.country_id
                        );
                    }
                }
            },
        },
    },

    created() {
        if (this.startInEditMode) {
            this.isEditMode = true;
        }
    },

    methods: {
        async onSubmit(e) {
            await this.v$.$touch();
            const isFormCorrect = await this.v$.$validate();
            if (!isFormCorrect) {
                this.$toastr.e(this.$t('Please fill in all required fields correctly'));
                return;
            }
            const { businessActivity } = this;
            this.$emit('submit', {
                values: {
                    ...this.data,
                    businessActivity,
                    e,
                },
            });
            this.stopEditMode();
        },
        startEditMode() {
            this.isEditMode = true;
        },
        stopEditMode() {
            this.isEditMode = false;
        },
        async getSelectedCountryStates(countryId) {
            this.$store.dispatch('setCountryStates', countryId);
        },
    },
};
</script>
